import React from 'react'
import { Box, Typography } from '@mui/material'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import BarChartOutlined from '@mui/icons-material/BarChartOutlined'
import DomainOutlined from '@mui/icons-material/DomainOutlined'
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined'
import _ from 'lodash'

const colors = {
  white: '#FFFFFF',
  primary: '#324755',
  accent: '#FF6F61',
  headerGradient: 'linear-gradient(90deg, #2C3E50, #34495E)',
  headerBorder: 'rgba(255,255,255,0.2)',
  border: '#e0e0e0',
}

const MIN_ANGLE = 1.5

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { name, value, total } = payload[0].payload
    return (
      <Box
        sx={{
          bgcolor: 'white',
          p: 1,
          border: `1px solid ${colors.border}`,
          borderRadius: '4px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: colors.primary, fontWeight: 600 }}
        >
          {name}
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          {`${value.toLocaleString()} (${((value / total) * 100).toFixed(1)}%)`}
        </Typography>
      </Box>
    )
  }
  return null
}

const CustomLegend = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0)
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 0.5,
        mt: 1,
      }}
    >
      {data.map((entry, index) => {
        const percentage = ((entry.value / total) * 100).toFixed(1)
        return (
          <Box
            key={`legend-${index}`}
            sx={{
              p: 0.25,
              border: `1px solid ${colors.border}`,
              borderRadius: 1,
              bgcolor: colors.white,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              transition: 'background-color 0.2s, transform 0.2s',
              cursor: 'default',
              '&:hover': {
                bgcolor: '#f4f4f4',
                transform: 'scale(1.02)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: entry.color,
                  mr: 0.5,
                }}
              />
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: colors.primary }}
              >
                {entry.name}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: colors.primary }}
              >
                {entry.value.toLocaleString()}
              </Typography>
              <Box
                sx={{
                  ml: 0.5,
                  px: 1,
                  py: '2px',
                  bgcolor: entry.color,
                  borderRadius: '12px',
                  color: colors.white,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                }}
              >
                {percentage}%
              </Box>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export const ChartHeader = ({ title }) => {
  let IconComponent = BarChartOutlined
  const lowerTitle = title.toLowerCase()
  if (lowerTitle.includes('occupancy type distribution')) {
    IconComponent = DomainOutlined
  } else if (lowerTitle.includes('damage severity distribution')) {
    IconComponent = ReportProblemOutlined
  }
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        background: colors.headerGradient,
        borderBottom: `2px solid ${colors.headerBorder}`,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          minWidth: 40,
          borderRadius: '50%',
          background: colors.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          mr: 2,
        }}
      >
        <IconComponent sx={{ fontSize: 24, color: colors.primary }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: colors.white,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          whiteSpace: 'normal',
          lineHeight: 1.2,
          fontSize: 16,
        }}
      >
        {title}
      </Typography>
    </Box>
  )
}

const ChartWidget = ({ title, data }) => {
  const processData = (inputData) => {
    const sortedData = _.orderBy(inputData, ['value'], ['desc'])
    const topItems = sortedData.slice(0, 5)
    const remainingItems = sortedData.slice(5)
    const otherValue = remainingItems.reduce((sum, item) => sum + item.value, 0)
    const processedData = [...topItems]
    if (otherValue > 0) {
      processedData.push({
        name: 'OTHER',
        value: otherValue,
        color: colors.border,
      })
    }
    return processedData
  }

  const processedData = processData(data)
  const total = processedData.reduce((sum, item) => sum + item.value, 0)
  const adjustedData = processedData.map((item) => {
    const percentage = (item.value / total) * 100
    const angle = (percentage / 100) * 360
    if (angle < MIN_ANGLE && item.value > 0) {
      return {
        ...item,
        value: (MIN_ANGLE / 360) * total,
        originalValue: item.value,
      }
    }
    return { ...item, originalValue: item.value }
  })
  const dataWithTotal = adjustedData.map((item) => ({ ...item, total }))

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
      }}
    >
      <ChartHeader title={title} />
      <Box sx={{ p: 2 }}>
        <Box sx={{ width: '100%', height: 200 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={dataWithTotal}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
                strokeWidth={0}
                startAngle={90}
                endAngle={-270}
                minAngle={MIN_ANGLE}
                isAnimationActive
              >
                {adjustedData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    style={{ outline: 'none' }}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} cursor={false} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <CustomLegend data={processedData} />
      </Box>
    </Box>
  )
}

export default ChartWidget
